import type { IContent, IContentState } from './types';
import type { ISearchResult } from '../content-browser/types';

import { unionBy } from 'lodash';

import * as ActionTypes from 'src/state/action-types';

export const initialState: IContentState = {
  content: [],
};

export default function contentReducer(
  state: IContentState = initialState,
  action: ActionTypes.ActionTypes
): IContentState {
  switch (action.type) {
    case ActionTypes.CONTENT_BROWSER_SEARCH_SUCCESS: {
      const content = action.payload.results
        .filter((result: ISearchResult) => result.index === 'content')
        .map((result: ISearchResult) => ({
          id: result.id,
          name: result.source.name,
          content_type: result.source.content_type,
          folder_id: result.source.folder_id || 'root',
          workspace_id: result.source.workspace_id || 'private',
          library: result.source.library,
        }));

      return {
        ...state,
        content: unionBy(state.content.concat(content), 'id'),
      };
    }

    case ActionTypes.H5PEDITOR_UPDATEEDITORCONTENT_SUCCESS: {
      const updatedContent: IContent = {
        id: action.payload.contentId,
        name: action.payload.h5p.metadata.title,
        content_type: 'h5p',
        folder_id: action.payload.folderId,
        workspace_id: action.payload.workspaceId,
        library: action.payload.h5p.library,
      };

      return {
        ...state,
        content: unionBy(state.content.concat([updatedContent]), 'id'),
      };
    }

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_REQUEST: {
      // Optimistic update: immediately update folder_id and workspace_id for moved content
      const { contentIds, targetFolderId, targetWorkspaceId } = action.payload;

      return {
        ...state,
        content: state.content.map((item) =>
          contentIds.includes(item.id)
            ? {
                ...item,
                folder_id: targetFolderId,
                workspace_id: targetWorkspaceId,
              }
            : item
        ),
      };
    }

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_FAILURE: {
      // Rollback optimistic update on failure
      const { originalData } = action.payload;

      return {
        ...state,
        content: state.content.map((item) => {
          const originalItem = originalData.contentItems.find((orig) => orig.id === item.id);
          return originalItem
            ? {
                ...item,
                folder_id: originalItem.folder_id,
                workspace_id: originalItem.workspace_id,
              }
            : item;
        }),
      };
    }

    case ActionTypes.CONTENT_DELETE_REQUEST: {
      // Optimistically remove items from state
      const { contentIds } = action.payload;

      return {
        ...state,
        content: state.content.filter((item) => !contentIds.includes(item.id)),
      };
    }

    case ActionTypes.CONTENT_DELETE_SUCCESS: {
      // Items already removed in REQUEST, no further action needed
      return state;
    }

    case ActionTypes.CONTENT_DELETE_FAILURE: {
      // Rollback: restore original items
      const { originalContentItems } = action.payload;

      return {
        ...state,
        content: unionBy(originalContentItems, state.content, 'id'),
      };
    }

    default:
      return state;
  }
}
